import React, { useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Canvas } from '@react-three/fiber';
import { OrbitControls, PerspectiveCamera } from '@react-three/drei';
import { Sparkles } from '@/components/ui/Sparkles';
import { ViewControlButtons } from '@/components/3d/ViewControls';
import { TaskCard, Task } from '@/components/education/TaskCard';
import { QuizGame, QuizQuestion } from '@/components/education/QuizGame';

// 乘法分组模型可视化
function MultiplicationVisualization({ groups, itemsPerGroup }: { groups: number; itemsPerGroup: number }) {
  const spacing = 2;

  return (
    <group>
      {Array.from({ length: groups }).map((_, groupIndex) => (
        <group key={groupIndex} position={[-(groups - 1) * spacing / 2 + groupIndex * spacing, 0, 0]}>
          {Array.from({ length: itemsPerGroup }).map((_, itemIndex) => (
            <mesh
              key={itemIndex}
              position={[0, itemIndex * 0.8, 0]}
            >
              <sphereGeometry args={[0.3, 16, 16]} />
              <meshStandardMaterial color={`hsl(${(groupIndex * 60 + itemIndex * 20) % 360}, 70%, 50%)`} />
            </mesh>
          ))}
        </group>
      ))}
    </group>
  );
}

export default function LessonMultiplication() {
  const navigate = useNavigate();
  const cameraRef = useRef<THREE.PerspectiveCamera>(null);
  const controlsRef = useRef<any>(null);
  const [groups, setGroups] = useState(2);
  const [itemsPerGroup, setItemsPerGroup] = useState(2);
  const [tasksCompleted, setTasksCompleted] = useState(false);
  const [quizScore, setQuizScore] = useState<number | null>(null);

  const product = groups * itemsPerGroup;

  // 视角控制函数
  const resetView = () => {
    if (cameraRef.current) {
      cameraRef.current.position.set(8, 6, 10);
      cameraRef.current.lookAt(0, 2, 0);
    }
    if (controlsRef.current) {
      controlsRef.current.target.set(0, 2, 0);
      controlsRef.current.update();
    }
  };

  const topView = () => {
    if (cameraRef.current) {
      cameraRef.current.position.set(0, 15, 0.001);
      cameraRef.current.lookAt(0, 2, 0);
    }
    if (controlsRef.current) {
      controlsRef.current.target.set(0, 2, 0);
      controlsRef.current.update();
    }
  };

  const frontView = () => {
    if (cameraRef.current) {
      cameraRef.current.position.set(0, 2, 15);
      cameraRef.current.lookAt(0, 2, 0);
    }
    if (controlsRef.current) {
      controlsRef.current.target.set(0, 2, 0);
      controlsRef.current.update();
    }
  };

  const sideView = () => {
    if (cameraRef.current) {
      cameraRef.current.position.set(15, 2, 0);
      cameraRef.current.lookAt(0, 2, 0);
    }
    if (controlsRef.current) {
      controlsRef.current.target.set(0, 2, 0);
      controlsRef.current.update();
    }
  };

  const isoView = () => {
    if (cameraRef.current) {
      cameraRef.current.position.set(10, 10, 10);
      cameraRef.current.lookAt(0, 2, 0);
    }
    if (controlsRef.current) {
      controlsRef.current.target.set(0, 2, 0);
      controlsRef.current.update();
    }
  };

  // 定义引导任务
  const tasks: Task[] = [
    {
      id: 1,
      title: '把好朋友人数调成3人',
      description: '拖动"👥 好朋友人数"滑块到3',
      checkCondition: () => groups === 3,
      hint: '太棒了！3个好朋友一起来！',
    },
    {
      id: 2,
      title: '给每人4块饼干',
      description: '拖动"🍪 每人饼干数"滑块到4',
      checkCondition: () => itemsPerGroup === 4,
      hint: '完美！每人4块饼干！',
    },
    {
      id: 3,
      title: '试试交换一下！4人每人3块',
      description: '试试交换一下！4个好朋友，每人3块饼干',
      checkCondition: () => groups === 4 && itemsPerGroup === 3,
      hint: '哇！你发现了乘法的秘密！总数还是12块！',
    },
  ];

  // 定义测验题目
  const quizQuestions: QuizQuestion[] = [
    {
      id: 1,
      question: '🍪 5个小朋友每人2块饼干，一共多少块？',
      options: ['8块', '10块', '12块', '7块'],
      correctAnswer: 1,
      hint: '提示: 2 + 2 + 2 + 2 + 2 = ?',
      explanation: '正确！5个小朋友每人2块：5 × 2 = 10块',
    },
    {
      id: 2,
      question: '🍪 3个小朋友每人5块饼干，一共多少块？',
      options: ['8块', '12块', '15块', '18块'],
      correctAnswer: 2,
      hint: '提示: 5 + 5 + 5 = ?',
      explanation: '太聪明了！3个小朋友每人5块：3 × 5 = 15块',
    },
    {
      id: 3,
      question: '🍪 2个小朋友每人6块饼干，和6个小朋友每人2块，一样多吗？',
      options: ['不一样', '一样多', '不知道'],
      correctAnswer: 1,
      hint: '提示: 2 × 6 = 12，6 × 2 = ?',
      explanation: '正确！2 × 6 = 12，6 × 2 = 12，这就是乘法的交换律！',
    },
    {
      id: 4,
      question: '🍪 4 × 3 表示什么意思？',
      options: ['4加3', '3个4相加', '4个3相加', '都不对'],
      correctAnswer: 2,
      hint: '提示: 4 × 3 = 4 + 4 + 4',
      explanation: '对！4 × 3 表示3个4相加：4 + 4 + 4 = 12',
    },
    {
      id: 5,
      question: '🍪 如果你有18块饼干，要平均分给6个小朋友，每人几块？',
      options: ['2块', '3块', '4块', '5块'],
      correctAnswer: 1,
      hint: '提示: 6 × ? = 18',
      explanation: '太棒了！18 ÷ 6 = 3，每人3块！',
    },
  ];

  return (
    <div className="min-h-screen bg-gradient-to-b from-slate-900 via-amber-900/20 to-orange-900/20">
      <nav className="fixed top-0 left-0 right-0 z-50 glass-panel">
        <div className="container mx-auto px-6 py-4 flex items-center justify-between">
          <div className="flex items-center gap-4">
            <Sparkles>
              <span className="text-4xl">🍪</span>
            </Sparkles>
            <div>
              <h1 className="text-3xl font-bold gradient-text">饼干分发游戏</h1>
              <p className="text-sm text-amber-300">学习乘法的秘密</p>
            </div>
          </div>
          <button
            onClick={() => navigate('/')}
            className="px-6 py-2 bg-gradient-to-r from-amber-500 to-orange-500 rounded-lg font-bold hover:opacity-90 transition-opacity"
          >
            ← 返回首页
          </button>
        </div>
      </nav>

      {/* 故事引入 */}
      <div className="pt-28 pb-6 px-6">
        <div className="max-w-7xl mx-auto">
          <div className="glass-panel rounded-2xl p-6 mb-6">
            <div className="flex items-start gap-4">
              <span className="text-5xl">📖</span>
              <div className="flex-1">
                <h2 className="text-2xl font-bold text-white mb-2">小明的饼干派对</h2>
                <p className="text-lg text-amber-200 leading-relaxed">
                  小明烤了很多好吃的<span className="text-2xl mx-1">🍪</span>饼干！
                  今天他邀请了<span className="text-2xl mx-1">👥</span>好朋友来家里玩。
                  让我们帮小明把饼干<span className="text-2xl mx-1">🎯</span>公平地分给大家吧！
                  <br />
                  <span className="text-yellow-300 font-bold">
                    乘法就是快速计算总数的魔法！
                  </span>
                </p>
              </div>
            </div>
          </div>
        </div>
      </div>

      <div className="pb-12 px-6">
        <div className="max-w-7xl mx-auto">
          <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
            {/* 左侧：3D场景和任务 */}
            <div className="lg:col-span-2 space-y-6">
              {/* 3D场景 */}
              <div className="glass-panel rounded-2xl p-6">
                <div className="flex items-center justify-between mb-4">
                  <h2 className="text-xl font-bold text-white flex items-center gap-2">
                    🎨 饼干分发场景
                  </h2>
                  <div className="text-center">
                    <div className="text-4xl font-bold text-yellow-400">
                      {groups} × {itemsPerGroup} = {product}
                    </div>
                    <div className="text-sm text-amber-300 mt-1">
                      {groups}个好朋友 × 每人{itemsPerGroup}块 = {product}块饼干
                    </div>
                  </div>
                </div>
                <div className="flex items-center justify-between mb-4">
                  <ViewControlButtons
                    onReset={resetView}
                    onTopView={topView}
                    onFrontView={frontView}
                    onSideView={sideView}
                    onIsoView={isoView}
                  />
                </div>
                <div className="w-full h-[400px] rounded-lg overflow-hidden bg-slate-800/50">
                  <Canvas shadows gl={{ antialias: true, alpha: true }} dpr={[1, 2]}>
                    <PerspectiveCamera makeDefault ref={cameraRef} position={[8, 6, 10]} fov={60} />
                    <ambientLight intensity={0.6} />
                    <directionalLight position={[10, 20, 10]} castShadow />
                    <MultiplicationVisualization groups={groups} itemsPerGroup={itemsPerGroup} />
                    <OrbitControls ref={controlsRef} enableDamping dampingFactor={0.05} />
                  </Canvas>
                </div>
                <p className="text-amber-200 text-sm mt-4 text-center">
                  💡 每一列代表一个好朋友，彩色圆球就是饼干！拖动滑块试试看～
                </p>
              </div>

              {/* 任务卡片 */}
              <TaskCard
                title="学习任务"
                tasks={tasks}
                onAllCompleted={() => setTasksCompleted(true)}
              />
            </div>

            {/* 右侧：控制面板和小测验 */}
            <div className="lg:col-span-1 space-y-6">
              {/* 控制面板 */}
              <div className="glass-panel rounded-2xl p-6">
                <h2 className="text-xl font-bold text-white mb-6 flex items-center gap-2">
                  🎛️ 控制面板
                </h2>

                {/* 好朋友人数 */}
                <div className="space-y-4 mb-6">
                  <label className="block text-white font-bold text-lg flex items-center gap-2">
                    👥 好朋友人数: {groups}
                  </label>
                  <input
                    type="range"
                    min="2"
                    max="6"
                    step="1"
                    value={groups}
                    onChange={(e) => setGroups(Number(e.target.value))}
                    className="w-full h-3 bg-slate-700 rounded-lg appearance-none cursor-pointer accent-amber-500"
                  />
                  <div className="flex justify-between text-xs text-slate-400">
                    <span>2人</span>
                    <span>6人</span>
                  </div>
                </div>

                {/* 每人饼干数 */}
                <div className="space-y-4 mb-6">
                  <label className="block text-white font-bold text-lg flex items-center gap-2">
                    🍪 每人饼干数: {itemsPerGroup}
                  </label>
                  <input
                    type="range"
                    min="2"
                    max="6"
                    step="1"
                    value={itemsPerGroup}
                    onChange={(e) => setItemsPerGroup(Number(e.target.value))}
                    className="w-full h-3 bg-slate-700 rounded-lg appearance-none cursor-pointer accent-orange-500"
                  />
                  <div className="flex justify-between text-xs text-slate-400">
                    <span>2块</span>
                    <span>6块</span>
                  </div>
                </div>

                {/* 乘法的意义 */}
                <div className="p-4 bg-gradient-to-br from-green-500/10 to-emerald-500/10 border-2 border-green-500/30 rounded-lg mb-6">
                  <h3 className="font-bold text-green-400 mb-3 flex items-center gap-2">
                    📖 乘法是什么？
                  </h3>
                  <div className="text-sm text-slate-200 space-y-2">
                    <p className="text-green-300">
                      乘法就是<span className="font-bold">快速加法</span>！
                    </p>
                    <p className="text-white text-base leading-relaxed">
                      {groups} × {itemsPerGroup} 就是{' '}
                      <span className="text-yellow-300 font-bold">
                        {groups}个{itemsPerGroup}相加
                      </span>
                      ：
                    </p>
                    <p className="text-lg text-center py-2 bg-slate-800/50 rounded-lg">
                      {Array.from({ length: groups }).map((_, i) => (
                        <span key={i} className="inline">
                          {itemsPerGroup}
                          {i < groups - 1 && ' + '}
                        </span>
                      ))}
                      {' = '}
                      <span className="text-yellow-400 font-bold">{product}</span>
                    </p>
                  </div>
                </div>

                {/* 小技巧 */}
                <div className="p-4 bg-gradient-to-br from-blue-500/10 to-cyan-500/10 border-2 border-blue-500/30 rounded-lg">
                  <h3 className="font-bold text-blue-400 mb-2 flex items-center gap-2">
                    💡 小秘密
                  </h3>
                  <ul className="text-slate-200 text-sm space-y-2">
                    <li className="flex items-start gap-2">
                      <span className="text-blue-400">•</span>
                      <span>
                        <span className="text-white font-bold">交换律：</span>
                        {groups} × {itemsPerGroup} = {itemsPerGroup} × {groups}
                      </span>
                    </li>
                    <li className="flex items-start gap-2">
                      <span className="text-blue-400">•</span>
                      <span>
                        <span className="text-white font-bold">试试看：</span>交换两个数字，结果不变！
                      </span>
                    </li>
                  </ul>
                </div>
              </div>

              {/* 小测验游戏 */}
              <QuizGame
                title="饼干挑战赛"
                questions={quizQuestions}
                onComplete={(score, total) => {
                  setQuizScore(score);
                }}
              />
            </div>
          </div>
        </div>
      </div>

      {/* 完成庆祝（所有任务和测验都完成后显示） */}
      {tasksCompleted && quizScore !== null && quizScore >= 4 && (
        <div className="fixed inset-0 bg-black/70 flex items-center justify-center z-50 p-6">
          <div className="glass-panel rounded-3xl p-8 max-w-lg w-full text-center animate-bounce">
            <div className="text-8xl mb-4">🏆</div>
            <h2 className="text-4xl font-bold text-yellow-400 mb-4">太棒了！</h2>
            <p className="text-2xl text-white mb-2">你完成了所有挑战！</p>
            <p className="text-xl text-amber-300 mb-4">
              测验得分：{quizScore} / 5 {'⭐'.repeat(quizScore)}
            </p>
            <p className="text-lg text-green-300 mb-6">你真是个乘法小达人！</p>
            <button
              onClick={() => navigate('/')}
              className="px-8 py-4 bg-gradient-to-r from-yellow-500 to-orange-500 rounded-xl font-bold text-xl text-white hover:opacity-90 transition-opacity"
            >
              继续探索其他课程 →
            </button>
          </div>
        </div>
      )}
    </div>
  );
}
